import React, { useState } from 'react';
import { Button, DatePicker, Form, FormInstance, Input, Select, Switch, Tooltip } from 'antd';
import { CloseOutlined, FileExcelOutlined, SearchOutlined } from '@ant-design/icons';
import { IntlShape, useIntl } from 'react-intl';
import { IObtSurveySearchCriteriaInput } from '../../../__generated__/types';
import { SelectOption } from '../interfaces';
import dayjs from 'dayjs';
import { PortalUser } from '../../../components';
import { getDateFormatOrDefault } from '../../users/UserFormatPreferenceServices';
import ObtSearchByFileModal, { FileSearchRow } from './ObtSearchByFileModal';

const { RangePicker } = DatePicker;

const { Option } = Select;


const PARTNER_TYPE_OPTIONS: SelectOption[] = [
    { name: 'category-customer', value: 'C' },
    { name: 'info-partner-type-vendor', value: 'V' },
];

const SURVEY_STATUS_OPTIONS: SelectOption[] = [
    { name: 'invoice-status-in-progress', value: 'IN_PROGRESS' },
    { name: 'survey-status-completed', value: 'COMPLETED' },
    { name: 'survey-status-live', value: 'LIVE' },
    { name: 'survey-status-new', value: 'NEW' },
    { name: 'survey-status-not-started', value: 'NOT_STARTED' },
    { name: 'survey-status-provider-feedback-error', value: 'PROVIDER_FEEDBACK_ERROR' },
    { name: 'survey-status-provider-feedback-ok', value: 'PROVIDER_FEEDBACK_OK' },
    { name: 'survey-status-sent-to-provider', value: 'SENT_TO_PROVIDER' },
];

type ObtSearchFormProps = {
    form: FormInstance<any>;
    currentUser: PortalUser;
    countryOptions: SelectOption[];
    companyCodeOptions: SelectOption[];
    systemOptions: SelectOption[];
    optionsLoading: boolean;
    onSearch: (values: IObtSurveySearchCriteriaInput) => void;
    onReset: () => void;
    onSearchByFile: (rows: FileSearchRow[]) => void;
};

// ─── Helpers (defined before use per lint rules) ─────────────────────────────

const toDateString = (d: dayjs.Dayjs | null | undefined): string | undefined =>
    d ? d.format('DD.MM.YYYY') : undefined;

const toDateStringPlusOneDay = (d: dayjs.Dayjs | null | undefined): string | undefined =>
    d ? d.add(1, 'day').format('DD.MM.YYYY') : undefined;

const trimOrUndefined = (v: string | undefined): string | undefined =>
    v?.trim() || undefined;

const joinOrUndefined = (arr: string[] | undefined): string | undefined =>
    arr?.length ? arr.join(',') : undefined;

const joinSurveyStatusesOrUndefined = (arr: string[] | undefined): string | undefined =>
    arr?.length
        ? arr
            .map((value) => value.trim())
            .filter((value) => value.length > 0)
            .join(',')
        : undefined;

const getSurveyStatusLabel = (intl: IntlShape, option: SelectOption): string =>
    intl.formatMessage({ id: option.name, defaultMessage: option.value.replace(/_/g, ' ') });

const getPartnerTypeLabel = (intl: IntlShape, option: SelectOption): string =>
    intl.formatMessage({ id: option.name, defaultMessage: option.value === 'C' ? 'Customer' : 'Vendor' });

type RangeValue = [dayjs.Dayjs | null, dayjs.Dayjs | null] | null | undefined;

const buildSearchCriteria = (values: any): IObtSurveySearchCriteriaInput => {
    const insertDateRange = values.insertDateRange as RangeValue;
    const surveyDateRange = values.surveyDateRange as RangeValue;

    return {
        partnerno:       trimOrUndefined(values.partnerno),
        partnername:     trimOrUndefined(values.partnername),
        partnertype:     values.partnertype || undefined,
        surveyStatus:    joinSurveyStatusesOrUndefined(values.surveyStatus),
        vatCode:         trimOrUndefined(values.vatCode),
        withContactOnly: values.withContactOnly ?? undefined,
        masterPartnerNo: trimOrUndefined(values.masterPartnerNo),
        country:         joinOrUndefined(values.country),
        companycode:     joinOrUndefined(values.companycode),
        systemCode:      joinOrUndefined(values.systemCode),
        insertDateFrom:  toDateString(insertDateRange?.[0]),
        insertDateTo:    toDateStringPlusOneDay(insertDateRange?.[1]),
        fromDate:        toDateString(surveyDateRange?.[0]),
        toDate:          toDateStringPlusOneDay(surveyDateRange?.[1]),
    };
};

// ─── Component ────────────────────────────────────────────────────────────────

const ObtSearchForm: React.FC<ObtSearchFormProps> = ({
    form, currentUser, countryOptions, companyCodeOptions, systemOptions,
    optionsLoading, onSearch, onReset, onSearchByFile,
}) => {
    const intl = useIntl();
    const dateFormat = getDateFormatOrDefault(currentUser);
    const [insertDateOpen, setInsertDateOpen] = useState(false);
    const [surveyDateOpen, setSurveyDateOpen] = useState(false);
    const [fileModalOpen, setFileModalOpen] = useState(false);


    const setInsertDateToday = () => {
        const today = dayjs();
        form.setFieldsValue({ insertDateRange: [today, today] });
        setInsertDateOpen(false);
    };

    const setSurveyDateToday = () => {
        const today = dayjs();
        form.setFieldsValue({ surveyDateRange: [today, today] });
        setSurveyDateOpen(false);
    };

    const formItemLayout = {
        labelCol: { span: 24 },
        wrapperCol: { span: 24 },
    };

    const handleFinish = (values: any) => {
        onSearch(buildSearchCriteria(values));
    };

    const handleClear = () => {
        form.resetFields();
        onReset();
    };

    const handleSearchByFileApply = (rows: FileSearchRow[]) => {
        setFileModalOpen(false);
        onSearchByFile(rows);
    };

    const cleanedUpCompanies = ()=> {
        const companies: any = {};
        if (companyCodeOptions) {
            companyCodeOptions.forEach(c => {
               companies[c.value] = c.name;
            });

            const sortedKeys = Object.keys(companies).sort();
            const resulting:SelectOption[] = [];
            sortedKeys.forEach(k => {
                resulting.push({name: companies[k], value: k});
            });
            return resulting;
        }
        return [];
    }


    return (
        <div className="obt-search-form">
            <Form {...formItemLayout} form={form} onFinish={handleFinish}>
                <div className="obt-form-grid">
                    {/* Column 1 */}
                    <div className="obt-form-column">
                        <div>
                            <label>
                                {intl.formatMessage({ id: 'obt.searchForm.partnerNo', defaultMessage: 'Partner No' })}
                            </label>
                            <Form.Item name="partnerno">
                                <Input
                                    allowClear={true}
                                    placeholder={intl.formatMessage({
                                        id: 'obt.searchForm.partnerNo',
                                        defaultMessage: 'Partner No',
                                    })}
                                />
                            </Form.Item>
                        </div>
                        <div>
                            <label>
                                {intl.formatMessage({
                                    id: 'obt.searchForm.partnerType',
                                    defaultMessage: 'Partner Type',
                                })}
                            </label>
                            <Form.Item name="partnertype">
                                <Select
                                    allowClear={true}
                                    placeholder={intl.formatMessage({
                                        id: 'obt.searchForm.partnerType',
                                        defaultMessage: 'Partner Type',
                                    })}
                                >
                                    {PARTNER_TYPE_OPTIONS.map((o) => (
                                        <Option key={o.value} value={o.value}>
                                            {getPartnerTypeLabel(intl, o)}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </div>
                        <div>
                            <label>
                                {intl.formatMessage({
                                    id: 'obt.searchForm.partnerName',
                                    defaultMessage: 'Partner Name',
                                })}
                            </label>
                            <Form.Item name="partnername">
                                <Input
                                    allowClear={true}
                                    placeholder={intl.formatMessage({
                                        id: 'obt.searchForm.partnerName',
                                        defaultMessage: 'Partner Name',
                                    })}
                                />
                            </Form.Item>
                        </div>
                        <div>
                            <label>
                                {intl.formatMessage({
                                    id: 'obt.searchForm.surveyStatus',
                                    defaultMessage: 'Survey Status',
                                })}
                            </label>
                            <Form.Item name="surveyStatus">
                                <Select
                                    mode="multiple"
                                    allowClear={true}
                                    placeholder={intl.formatMessage({
                                        id: 'obt.searchForm.surveyStatus',
                                        defaultMessage: 'Survey Status',
                                    })}
                                >
                                    {SURVEY_STATUS_OPTIONS.map((o) => (
                                        <Option key={o.value} value={o.value}>
                                            {getSurveyStatusLabel(intl, o)}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </div>
                        <div>
                            <label>
                                {intl.formatMessage({ id: 'obt.searchForm.vatCode', defaultMessage: 'VAT Code' })}
                            </label>
                            <Form.Item name="vatCode">
                                <Input
                                    allowClear={true}
                                    disabled={true}
                                    placeholder={intl.formatMessage({
                                        id: 'obt.searchForm.vatCode',
                                        defaultMessage: 'VAT Code',
                                    })}
                                />
                            </Form.Item>
                        </div>
                        <div>
                            <label>
                                {intl.formatMessage({
                                    id: 'obt.searchForm.withContact',
                                    defaultMessage: 'With Contact Only',
                                })}
                            </label>
                            <Form.Item name="withContactOnly" valuePropName="checked">
                                <Switch />
                            </Form.Item>
                        </div>
                    </div>

                    {/* Column 2 */}
                    <div className="obt-form-column">
                        <div>
                            <label>
                                {intl.formatMessage({ id: 'obt.searchForm.masterId', defaultMessage: 'Master ID' })}
                            </label>
                            <Form.Item name="masterPartnerNo">
                                <Input
                                    allowClear={true}
                                    placeholder={intl.formatMessage({
                                        id: 'obt.searchForm.masterId',
                                        defaultMessage: 'Master ID',
                                    })}
                                />
                            </Form.Item>
                        </div>
                        <div>
                            <label>
                                {intl.formatMessage({ id: 'obt.searchForm.country', defaultMessage: 'Country' })}
                            </label>
                            <Form.Item name="country">
                                <Select
                                    mode="multiple"
                                    allowClear={true}
                                    loading={optionsLoading}
                                    placeholder={intl.formatMessage({
                                        id: 'obt.searchForm.country',
                                        defaultMessage: 'Country',
                                    })}
                                    showSearch={true}
                                    filterOption={(input, option) =>
                                        String(option?.children ?? '')
                                            .toLowerCase()
                                            .includes(input.toLowerCase())
                                    }
                                >
                                    {countryOptions.map((o) => (
                                        <Option key={o.value} value={o.value}>
                                            {o.name}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </div>
                        <div>
                            <label>
                                {intl.formatMessage({
                                    id: 'obt.searchForm.companyCode',
                                    defaultMessage: 'Company Code',
                                })}
                            </label>
                            <Form.Item name="companycode">
                                <Select
                                    mode="multiple"
                                    allowClear={true}
                                    loading={optionsLoading}
                                    placeholder={intl.formatMessage({
                                        id: 'obt.searchForm.companyCode',
                                        defaultMessage: 'Company Code',
                                    })}
                                    showSearch={true}
                                    filterOption={(input, option) =>
                                        String(option?.children ?? '')
                                            .toLowerCase()
                                            .includes(input.toLowerCase())
                                    }
                                >
                                    {cleanedUpCompanies().map((o) => (
                                        <Option key={o.value} value={o.value}>
                                            {o.name}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </div>
                        <div>
                            <label>
                                {intl.formatMessage({ id: 'obt.searchForm.system', defaultMessage: 'System' })}
                            </label>
                            <Form.Item name="systemCode">
                                <Select
                                    mode="multiple"
                                    allowClear={true}
                                    loading={optionsLoading}
                                    placeholder={intl.formatMessage({
                                        id: 'obt.searchForm.system',
                                        defaultMessage: 'System',
                                    })}
                                    showSearch={true}
                                    filterOption={(input, option) =>
                                        String(option?.children ?? '')
                                            .toLowerCase()
                                            .includes(input.toLowerCase())
                                    }
                                >
                                    {systemOptions.map((o) => (
                                        <Option key={o.value} value={o.value}>
                                            {o.name}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </div>
                        {/* Master Data Insert Date Range */}
                        <div>
                            <label>
                                {intl.formatMessage({
                                    id: 'obt.searchForm.insertDateRange',
                                    defaultMessage: 'Master Data Insert Date',
                                })}
                            </label>
                            <Form.Item name="insertDateRange">
                                <RangePicker
                                    format={dateFormat}
                                    style={{ width: '100%' }}
                                    allowClear={true}
                                    open={insertDateOpen}
                                    onOpenChange={setInsertDateOpen}
                                    renderExtraFooter={() => (
                                        <Button
                                            type="primary"
                                            onClick={setInsertDateToday}
                                            style={{ marginLeft: '240px', marginTop: '10px', marginBottom: '10px' }}
                                        >
                                            {intl.formatMessage({
                                                id: 'obt.searchForm.today',
                                                defaultMessage: 'Today',
                                            })}
                                        </Button>
                                    )}
                                    placeholder={[
                                        intl.formatMessage({ id: 'obt.searchForm.dateFrom', defaultMessage: 'From' }),
                                        intl.formatMessage({ id: 'obt.searchForm.dateTo', defaultMessage: 'To' }),
                                    ]}
                                    onKeyDown={(e) => e.preventDefault()}
                                />
                            </Form.Item>
                        </div>
                    </div>

                    {/* Column 3 */}
                    <div className="obt-form-column">
                        {/* Survey Date Range */}
                        <div>
                            <label>
                                {intl.formatMessage({
                                    id: 'obt.searchForm.surveyDateRange',
                                    defaultMessage: 'Survey Date',
                                })}
                            </label>
                            <Form.Item name="surveyDateRange">
                                <RangePicker
                                    format={dateFormat}
                                    style={{ width: '100%' }}
                                    allowClear={true}
                                    open={surveyDateOpen}
                                    onOpenChange={setSurveyDateOpen}
                                    renderExtraFooter={() => (
                                        <Button
                                            type="primary"
                                            onClick={setSurveyDateToday}
                                            style={{ marginLeft: '240px', marginTop: '10px', marginBottom: '10px' }}
                                        >
                                            {intl.formatMessage({
                                                id: 'obt.searchForm.today',
                                                defaultMessage: 'Today',
                                            })}
                                        </Button>
                                    )}
                                    placeholder={[
                                        intl.formatMessage({ id: 'obt.searchForm.dateFrom', defaultMessage: 'From' }),
                                        intl.formatMessage({ id: 'obt.searchForm.dateTo', defaultMessage: 'To' }),
                                    ]}
                                    onKeyDown={(e) => e.preventDefault()}
                                />
                            </Form.Item>
                        </div>
                    </div>
                </div>

                {/* Action buttons — below the fields, matching EIM pattern */}
                <div className="obt-search-actions">
                    <Tooltip title={intl.formatMessage({ id: 'info-portal-search', defaultMessage: 'Search' })}>
                        <Button
                            data-testid="obt-search-button"
                            type="primary"
                            htmlType="submit"
                            icon={<SearchOutlined />}
                            size="large"
                        >
                            {intl.formatMessage({ id: 'info-portal-search', defaultMessage: 'Search' })}
                        </Button>
                    </Tooltip>
                    <Tooltip title={intl.formatMessage({ id: 'do-clear', defaultMessage: 'Clear' })}>
                        <Button
                            data-testid="obt-clear-button"
                            type="default"
                            icon={<CloseOutlined />}
                            onClick={handleClear}
                            size="large"
                        >
                            {intl.formatMessage({ id: 'do-clear', defaultMessage: 'Clear' })}
                        </Button>
                    </Tooltip>
                    <Tooltip
                        title={intl.formatMessage({
                            id: 'obt.searchByFile.tooltip',
                            defaultMessage: 'Upload an Excel file to search by partner list',
                        })}
                    >
                        <Button
                            data-testid="obt-search-by-file-button"
                            type="default"
                            icon={<FileExcelOutlined />}
                            onClick={() => setFileModalOpen(true)}
                            size="large"
                        >
                            {intl.formatMessage({ id: 'obt.searchByFile', defaultMessage: 'Search by File' })}
                        </Button>
                    </Tooltip>
                </div>
            </Form>

            <ObtSearchByFileModal
                open={fileModalOpen}
                onClose={() => setFileModalOpen(false)}
                onApply={handleSearchByFileApply}
            />
        </div>
    );
};

export default ObtSearchForm;
